// /portfolios — list all portfolios + create dialog (T065).
//
// Migrated onto the UI primitives (PageHeader, Button, Card, Dialog,
// FormField, Input, EmptyState, LoadingCard, ErrorState). This page had
// drifted from the rest of the app — a bespoke `fixed inset-0` overlay
// dialog and a `focus:ring` input style instead of the native <dialog> +
// `focus:border` field used elsewhere. The migration unifies both.
//
// Each portfolio card links to /portfolios/:id for the detail dashboard
// (tech-debt breakdown + security posture heatmap).
import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR, { mutate } from 'swr';
import { ChevronRight, Layers, Plus } from 'lucide-react';
import { ApiError, Portfolio, PortfolioCreate, fetcher, portfoliosApi } from '../lib/api';
import {
  Button,
  Card,
  Dialog,
  DialogFooter,
  DialogHeader,
  EmptyState,
  ErrorState,
  FormField,
  Input,
  LoadingCard,
  PageHeader,
} from '../components/ui';
import { FeatureGate } from '../components/FeatureLock';
import { useLicenseState } from '../lib/useLicenseState';

const PORTFOLIOS_URL = '/api/v1/portfolios';

export function Portfolios() {
  const { data, error, isLoading } = useSWR<Portfolio[]>(PORTFOLIOS_URL, fetcher);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { loading: licenseLoading, hasFeature } = useLicenseState();
  const canCreate = !licenseLoading && hasFeature('portfolio');

  return (
    <div className="max-w-5xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · governance"
        title="Bundles"
        description="Group applications and projects to track security posture across your organisation."
        actions={
          canCreate ? (
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              New bundle
            </Button>
          ) : null
        }
      />

      <FeatureGate feature="portfolio" label="Bundles">
        {isLoading ? (
          <LoadingCard label="Loading bundles…" />
        ) : null}
        {!isLoading && error ? (
          <ErrorState error={error} />
        ) : null}
        {!isLoading && !error && (!data || data.length === 0) ? (
          <EmptyState
            icon={<Layers className="h-8 w-8" aria-hidden="true" />}
            title="No bundles yet."
            description="Create a bundle to group your applications and track tech-debt and security posture in one place."
            action={
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                New bundle
              </Button>
            }
          />
        ) : null}
        {!isLoading && !error && data && data.length > 0 ? (
          <PortfolioGrid portfolios={data} />
        ) : null}

        <CreatePortfolioDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onCreated={() => {
            void mutate(PORTFOLIOS_URL);
            setDialogOpen(false);
          }}
        />
      </FeatureGate>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Portfolio grid
// ---------------------------------------------------------------------------

function PortfolioGrid({ portfolios }: { readonly portfolios: Portfolio[] }) {
  const sorted = [...portfolios].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {sorted.map((p) => (
        <PortfolioCard key={p.id} portfolio={p} />
      ))}
    </div>
  );
}

function PortfolioCard({ portfolio: p }: { readonly portfolio: Portfolio }) {
  return (
    <Card
      as={Link}
      to={`/portfolios/${p.id}`}
      className="p-5 flex flex-col gap-2 group hover:border-accent/40 hover:bg-bg-hover transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Layers className="h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
          <span className="font-display text-base font-semibold text-fg truncate">{p.name}</span>
        </div>
        <ChevronRight
          className="h-4 w-4 shrink-0 text-fg-subtle group-hover:text-fg transition-colors"
          aria-hidden="true"
        />
      </div>
      <span className="orb-code text-xs text-fg-subtle">{p.key}</span>
      {p.description ? (
        <p className="text-xs text-fg-muted line-clamp-2">{p.description}</p>
      ) : null}
      <p className="mt-auto text-xs text-fg-subtle">Created {formatDate(p.created_at)}</p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Create dialog
// ---------------------------------------------------------------------------

interface CreatePortfolioDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onCreated: () => void;
}

function CreatePortfolioDialog({ open, onClose, onCreated }: CreatePortfolioDialogProps) {
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const body: PortfolioCreate = { key: key.trim(), name: name.trim() };
      if (description.trim()) body.description = description.trim();
      await portfoliosApi.create(body);
      onCreated();
    } catch (err) {
      let errMsg: string;
      if (err instanceof ApiError) {
        errMsg = `${err.code}: ${err.message}`;
      } else if (err instanceof Error) {
        errMsg = err.message;
      } else {
        errMsg = 'create failed';
      }
      setError(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader eyebrow="new · bundle" title="New Bundle" />
      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        <FormField label="Key" htmlFor="np-key" hint="Unique slug used in API references.">
          <Input
            id="np-key"
            type="text"
            required
            autoFocus
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="e.g. platform-security"
          />
        </FormField>
        <FormField label="Name" htmlFor="np-name">
          <Input
            id="np-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Platform Security"
          />
        </FormField>
        <FormField label="Description" htmlFor="np-desc">
          <Input
            id="np-desc"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
          />
        </FormField>

        {error ? (
          <p role="alert" className="text-xs text-danger">
            {error}
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            Create
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}
